import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import './EditableForm.css';
import { fetchAPI, postAPI } from '../../../utils/utils';
import { formatMoney } from '../../../utils/money';
import {
  createSubmitFormValues,
  getRequiredVisibleElements,
} from '../utils/formVisibility';
import useConditionalFormState from './useConditionalFormState';
import { AUTO_PAYMENT_METHOD_ID, isAutoPaymentMethod, webPaymentMethodOptions } from '../utils/autoPaymentMethod';
import {
  DEFAULT_COMMISSION_RATE,
  cardCommission,
  cardPaymentNote,
  paymentLines,
  submitButtonLabel,
  unavailableSubmitLabel,
} from '../utils/formCheckout';
import { canAnswerElement, firstMissingElement } from '../utils/requiredAnswers';
import { membersNamed } from '../utils/guestDuplicates';
import { collaAccent } from '../utils/collaAccent';
import CardPayment from './CardPayment';
import FormBottomBar from './FormBottomBar';
import PaymentMethodCards from './PaymentMethodCards';
import Chevron from './Chevron';
import OpenInApp from './OpenInApp';
import MemberLogin from './MemberLogin';

// The payment page Aleta makes (backend/form-auto-payment.js): drawn as the app's
// checkout, its answers stay those of the elements it is made of
const AUTO_PAYMENT_IDS = Object.freeze({
  iban: 'auto-payment-iban',
  concept: 'auto-payment-concept',
  receipt: 'auto-payment-receipt',
  cashReceipt: 'auto-payment-cash-receipt',
});
const isAutoIban = (element) => element.autoPayment
  && (element.id === AUTO_PAYMENT_IDS.iban || String(element.id).startsWith(`${AUTO_PAYMENT_IDS.iban}-`));
// The step a history entry opens (the browser's back goes from the payment to the questions)
const PAYMENT_STEP_STATE = 'aleta-form-payment-step';

const preprocessHTML = (html = '') => {
  const urlRegex = /(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])/g;
  const boldRegex = /(\*)(.*?)\1/g;
  const emphasisRegex = /(_)(.*?)\1/g;
  // Safari-safe @username regex (no lookbehind). Captures optional prefix and username
  const usernameRegex = /(^|[^\w.])@([\w]+(?:\.[\w]+)*)\b/g;
  const emailRegex = /(\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b)/g;

  return String(html || '')
    .replace(/\n/g, '<br />')
    .split(/(<a\b[^>]*>.*?<\/a>)/gi)
    .map((part, index) => {
      if (index % 2 === 0) {
        return part
          .replace(urlRegex, (match) => `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`)
          .replace(boldRegex, (match, _p1, p2) => `<b>${p2}</b>`)
          .replace(emphasisRegex, (match, _p1, p2) => `<em>${p2}</em>`)
          .replace(usernameRegex, (match, prefix, username) => `${prefix}<a href="https://www.instagram.com/${username}/" target="_blank" rel="noopener noreferrer">@${username}</a>`)
          .replace(emailRegex, (match) => `<a href="mailto:${match}">${match}</a>`);
      }
      return part;
    })
    .join('');
};

const formatPriceDisplay = (amount) => {
  const numericAmount = Number(amount);
  if (!isFinite(numericAmount)) return `${amount}€`;
  return numericAmount === 0 ? 'Gratuït' : `${formatMoney(numericAmount)}€`;
};

const EditableForm = ({
  isLogged,
  mote,
  setMote,
  castellersInfo,
  formData,
  evId,
  user,
  socket,
  formValues,
  formValuesLoaded = true,
  setFormValues,
  formSubmitted,
  savedAnswer = false,
  paymentSession = null,
  paymentCancelled = false,
  warningAccepted,
  setWarningAccepted,
  onNewAnswer = () => {},
  onSubmitted = () => {},
}) => {
  const outdated = formData?.closingDate && moment(formData?.closingDate).isBefore(moment());
  const closeToEnding = !outdated && formData?.closingDate && moment(formData?.closingDate).isBefore(moment().add(2, 'day'));
  const notOpenYet = formData?.openingDate && moment(formData?.openingDate).isAfter(moment());
  const formDisabled = outdated || notOpenYet || formData?.hidden;

  // Back from Stripe's page, paid or not: the answer was already sent
  const [sent, setSent] = useState(Boolean((paymentSession || paymentCancelled) && savedAnswer));
  // Sent from this page: changing it again is changing a saved answer
  const [sentHere, setSentHere] = useState(false);
  // Without Aleta, the name first, on its own screen (already there in a saved answer)
  const [nameGiven, setNameGiven] = useState(Boolean(savedAnswer || String(mote || '').trim()));
  // «Ets tu?»: the member of the colla the name is, logging in (MemberLogin.js)
  const [loginMember, setLoginMember] = useState(null);
  // Just sent with «Targeta» chosen: straight to Stripe
  const [startCardPayment, setStartCardPayment] = useState(false);
  // «No tinc l'app»: kept by FillForm across answers when it passes it
  const [ownWarningAccepted, setOwnWarningAccepted] = useState(false);
  const isWarningAccepted = warningAccepted ?? ownWarningAccepted;
  const setIsWarningAccepted = setWarningAccepted || setOwnWarningAccepted;
  const [copiedText, setCopiedText] = useState({});
  const [uploadState, setUploadState] = useState({});
  const [formImagesValues, setFormImagesValues] = useState({});
  const [staticImages, setStaticImages] = useState({});
  const [errors, setErrors] = useState({});
  // The questions, then the payment as its own step, as in the app
  const [step, setStep] = useState('form');
  // The question to bring into view once its step is on screen
  const [missingInView, setMissingInView] = useState(null);
  const [commissionRate, setCommissionRate] = useState(DEFAULT_COMMISSION_RATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingTickets, setRemainingTickets] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(isLogged && user in castellersInfo);

  const elements = useMemo(
    () => (formData?.elements || []).filter((element) => !element.deleted),
    [formData?.elements]
  );

  useEffect(() => {
    setIsLoggedIn(Boolean(isLogged && user && castellersInfo?.[user]));
  }, [castellersInfo, isLogged, user]);

  const {
    canApplyImage,
    isElementVisible,
    sanitizeValues,
    shouldRequestImage,
    totalPriceToPay,
    visibleTicketElements: visibleFormTickets,
  } = useConditionalFormState({
    elements,
    formData,
    formValues,
    formValuesLoaded,
    scopeKey: `${evId}:${user}`,
    setFormImagesValues,
    setFormValues,
    setUploadState,
  });

  // «Targeta» chosen (or a hand-made card payment shown) with something to pay
  const showsCardPayment = elements.some((element) => element.type === 'stripe-payment' && isElementVisible(element));
  const paysByCard = showsCardPayment && totalPriceToPay > 0;
  const commissionMode = elements.find((element) => element.type === 'stripe-payment')?.content?.commissionMode || 'user_pays';
  const offersCard = elements.some((element) => element.type === 'stripe-payment');

  // The commission's rate, for the total before paying by card
  useEffect(() => {
    if (!offersCard) return undefined;
    let current = true;
    fetchAPI('/stripe/commission-info', (data) => {
      if (!current || !data?.success || !Number.isFinite(data.percent) || !Number.isFinite(data.fixed)) return;
      setCommissionRate({ percent: data.percent, fixed: data.fixed });
    }, false, () => {});
    return () => { current = false; };
  }, [offersCard]);

  const userInfo = isLoggedIn ? (castellersInfo?.[user] || {}) : {};

  const formatText = (rawText) => {
    // A guest is the name they typed (the transfer concept says who paid)
    const nomValue = isLoggedIn ? (userInfo?.nom || '') : (mote || '');
    const cognomValue = userInfo?.cognom || userInfo?.['primer-cognom'] || '';
    const sobrenomValue = isLoggedIn ? (userInfo?.mote || userInfo?.nom || '') : (mote || '');

    return String(rawText || '')
      .replaceAll('{nom}', nomValue)
      .replaceAll('{cognom}', cognomValue)
      .replaceAll('{sobrenom}', sobrenomValue)
      .replaceAll('{mote}', sobrenomValue);
  };

  const formatRichText = (text) => preprocessHTML(formatText(text));

  useEffect(() => {
    if (sent) return;
    elements.forEach((element) => {
      const { id, type } = element;
      if (type === 'image-upload') {
        if (!shouldRequestImage(element)) return;
        if (formImagesValues[id]) return;
        // A guest has images only in the answer they sent before
        if (!isLoggedIn && !savedAnswer) return;
        fetchAPI(`/form_image_url/${evId}/${id}/${user}`, ({ url }) => {
          if (!url) return;
          fetch(url)
            .then((res) => {
              if (!res.ok) throw new Error('Error fetching image from S3');
              return res.text();
            })
            .then((data) => {
              if (!canApplyImage(element)) return;
              setFormImagesValues((prev) => ({ ...prev, [id]: data }));
              setUploadState((prev) => ({ ...prev, [id]: 'uploaded' }));
            })
            .catch(() => {});
        }, false, false);
      } else if (type === 'image') {
        if (staticImages[id]) return;
        fetchAPI(`/form_image_url/${evId}/${id}`, ({ url }) => {
          if (!url) return;
          fetch(url)
            .then((res) => {
              if (!res.ok) throw new Error('Error fetching image from S3');
              return res.text();
            })
            .then((data) => {
              setStaticImages((prev) => ({ ...prev, [id]: data }));
            })
            .catch(() => {});
        }, false, false);
      }
    });
  }, [canApplyImage, elements, evId, formImagesValues, isLoggedIn, savedAnswer, sent, shouldRequestImage, staticImages, user]);

  useEffect(() => {
    if (sent) return;

    const updateRemainingTickets = (optionId, nextRemaining) => {
      setRemainingTickets((prev) => {
        if (prev?.[optionId] === nextRemaining) return prev;
        return {
          ...prev,
          [optionId]: nextRemaining,
        };
      });
    };

    const ticketElements = elements.filter((element) => ['ticket', 'ticket-v2', 'ticket-v2-single'].includes(element.type));
    ticketElements.forEach((element) => {
      if (element.type === 'ticket') {
        if (!element.limit) return;
        const optionId = `${element.id}_0`;
        postAPI('/total_sold', {
          elementId: element.id,
          optionIndex: null,
          castellerId: user,
        }, (data) => {
          const totalSold = data?.total_sold || 0;
          const limit = element.limit || Infinity;
          updateRemainingTickets(optionId, limit - totalSold - (formValues?.[element.id] === 0 ? 1 : 0));
        });
      } else {
        element.content.options.forEach((option, index) => {
          if (option.hidden) return;
          if (!option.limit) return;
          const optionId = `${element.id}_${index}`;
          postAPI('/total_sold', {
            elementId: element.id,
            optionIndex: index,
            castellerId: user,
          }, (data) => {
            const totalSold = data?.total_sold || 0;
            const limit = option.limit || Infinity;
            updateRemainingTickets(optionId, limit - totalSold - (formValues?.[element.id]?.[index] || 0));
          });
        });
      }
    });
  }, [elements, user, formValues, sent]);

  const clearError = (elementId) => {
    setErrors((prev) => {
      if (!(elementId in prev)) return prev;
      const { [elementId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleInputChange = (elementId, value, index) => {
    setFormValues((prevState) => {
      const newValues = { ...prevState };
      const element = elements.find((el) => el.id === elementId);
      const type = element?.type;
      const isBox = type === 'checkbox' || type === 'multiple-choice' || type === 'ticket';

      if (index !== undefined) {
        newValues[elementId] = { ...(newValues[elementId] || {}) };
        newValues[elementId][index] = isBox && newValues[elementId]?.[index] === value ? undefined : value;
      } else if (isBox && newValues[elementId] === value) {
        delete newValues[elementId];
      } else {
        newValues[elementId] = value;
      }

      return sanitizeValues(newValues);
    });
    clearError(elementId);
  };

  const handleTicketQuantityChange = (elementId, optionIndex, change) => {
    setFormValues((prevState) => {
      const newValues = { ...prevState };
      newValues[elementId] = { ...(newValues[elementId] || {}) };
      const currentValue = newValues[elementId][optionIndex] || 0;
      const newValue = Math.max(0, currentValue + change);

      const element = elements.find((el) => el.id === elementId);
      const option = element?.content?.options?.[optionIndex];
      if (option?.limit && newValue > option.limit) {
        return prevState;
      }

      newValues[elementId][optionIndex] = newValue;
      return sanitizeValues(newValues);
    });
    clearError(elementId);
  };

  const handleFileInput = (elementId, file) => {
    if (!file) return;
    const reader = new FileReader();
    setUploadState((prev) => ({ ...prev, [elementId]: 'uploading' }));
    reader.onload = () => {
      setFormImagesValues((prev) => ({ ...prev, [elementId]: reader.result }));
      setUploadState((prev) => ({ ...prev, [elementId]: 'uploaded' }));
    };
    reader.onerror = () => {
      setUploadState((prev) => ({ ...prev, [elementId]: 'error' }));
    };
    reader.readAsDataURL(file);
    clearError(elementId);
  };

  const deleteImage = (elementId) => {
    fetchAPI(`/delete_form_image/${evId}/${elementId}/${user}`, () => {
      setFormImagesValues((prev) => ({ ...prev, [elementId]: undefined }));
      setUploadState((prev) => ({ ...prev, [elementId]: undefined }));
    }, false, false);
  };

  const handleCopiedText = (elementId, text) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedText((prev) => ({ ...prev, [elementId]: true }));
      setTimeout(() => {
        setCopiedText((prev) => ({ ...prev, [elementId]: false }));
      }, 1000);
    }
  };

  const renderTicketAvailability = (remaining, quantity = 0) => {
    if (remaining === undefined || remaining === Infinity) return 'Carregant...';
    if (remaining <= 0 && quantity === 0) return 'Ja no en queden';
    if (remaining <= 0 && quantity > 0) return "Agafes l'últim!";
    if (remaining === 1) return 'En queda un!';
    if (remaining > 10) return '';
    return `En queden ${remaining}`;
  };

  const renderImageUpload = (element, { title = element.content?.question, hint = null } = {}) => {
    const { id, required } = element;
    return (
      <div key={id} className="form-element-container">
        <div className="form-question-header">
          <h2>{title}</h2>
          {required && <span className="form-required">*</span>}
        </div>
        {hint ? <div className="form-question-hint">{hint}</div> : null}
        {formImagesValues?.[id] && (
          <div className="image-preview">
            <img src={formImagesValues[id]} alt="Pujada" />
            <button type="button" className="editable-form-delete-image-button" onClick={() => deleteImage(id)} disabled={formDisabled}>
              Esborra la imatge
            </button>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          aria-label={title}
          onChange={(event) => handleFileInput(id, event.target.files?.[0])}
          disabled={formDisabled}
        />
        {uploadState?.[id] === 'uploading' ? <div className="upload-status">Pujant imatge...</div> : null}
        {uploadState?.[id] === 'error' ? <div className="upload-status">Error penjant la imatge</div> : null}
        {errors[id] && <div className="form-error-text">{errors[id]}</div>}
      </div>
    );
  };

  // The store's "Dades per a la transferència": the amount, the IBANs and the concept, each to copy
  const renderTransferDetails = (element) => {
    const accounts = elements.filter((candidate) => isAutoIban(candidate) && isElementVisible(candidate));
    const concept = elements.find((candidate) => candidate.id === AUTO_PAYMENT_IDS.concept);
    const row = (key, label, value, copyLabel, unit = null) => (
      <div key={key} className="transfer-row">
        <div className="transfer-row-label">{label}</div>
        <div className="transfer-row-value">
          <span>{value}</span>
          {unit ? <span className="transfer-row-unit">{unit}</span> : null}
        </div>
        <button type="button" onClick={() => handleCopiedText(key, value)} className="editable-form-copy-button">
          {copiedText?.[key] ? 'Copiat!' : copyLabel}
        </button>
      </div>
    );
    return (
      <div key={element.id} className="form-element-container">
        <div className="form-question-header">
          <h2>Dades per a la transferència</h2>
        </div>
        {totalPriceToPay > 0 ? row('transfer-amount', 'Import', formatMoney(totalPriceToPay, { fixed: true }), "Copia l'import", '€') : null}
        {accounts.map((account) => row(
          account.id,
          account.content?.accountName ? `IBAN (${account.content.accountName})` : 'IBAN',
          account.content?.text,
          "Copia l'IBAN",
        ))}
        {concept && isElementVisible(concept)
          ? row(concept.id, 'Concepte', formatText(concept.content?.text).replace(/\s+/g, ' ').trim(), 'Copia el concepte')
          : null}
      </div>
    );
  };

  // undefined: drawn as any other question of its type
  const renderAutoPaymentElement = (element) => {
    const { id } = element;
    if (String(id).startsWith(`${AUTO_PAYMENT_IDS.iban}-`) || id === AUTO_PAYMENT_IDS.concept) return null;
    if (id === AUTO_PAYMENT_IDS.iban) return renderTransferDetails(element);
    if (id === AUTO_PAYMENT_IDS.receipt) {
      return renderImageUpload(element, {
        title: 'Comprovant de pagament',
        hint: 'Fes la transferència i puja una captura o foto del comprovant.',
      });
    }
    if (id === AUTO_PAYMENT_IDS.cashReceipt) {
      return renderImageUpload(element, {
        title: 'Foto del moment',
        hint: 'Quan paguis, fes una foto del moment, per exemple amb qui ha cobrat, o del tiquet del datàfon.',
      });
    }
    return undefined;
  };

  const renderElement = (element) => {
    const { id, type, content, required } = element;
    const { question, options, text } = content || {};

    if (!isElementVisible(element)) return null;
    if (element.autoPayment) {
      const rendered = renderAutoPaymentElement(element);
      if (rendered !== undefined) return rendered;
    }

    const errorMessage = errors[id];

    switch (type) {
      case 'multiple-choice':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{id === AUTO_PAYMENT_METHOD_ID && element.autoPayment ? 'Mètode de pagament' : question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {isAutoPaymentMethod(element) ? (
              <PaymentMethodCards
                name={id}
                options={webPaymentMethodOptions(element, formValues?.[id])}
                value={formValues?.[id]}
                disabled={formDisabled}
                onChange={(index) => handleInputChange(id, index)}
              />
            ) : (options || []).map((option, index) => {
              const checked = formValues?.[id] === index;
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={id}
                    checked={checked || false}
                    onChange={() => (formDisabled ? undefined : handleInputChange(id, index))}
                    disabled={formDisabled}
                  />
                  <div>{option}</div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'checkbox':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(options || []).map((option, index) => {
              const checked = formValues?.[id]?.[index] || false;
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => (formDisabled ? undefined : handleInputChange(id, !checked, index))}
                    disabled={formDisabled}
                  />
                  <div>{option}</div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'short-answer':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            <input
              type="text"
              value={formValues?.[id] || ''}
              onChange={(event) => (formDisabled ? undefined : handleInputChange(id, event.target.value))}
              disabled={formDisabled}
              placeholder="Escriu aquí la resposta..."
            />
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'paragraph':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            <textarea
              value={formValues?.[id] || ''}
              onChange={(event) => (formDisabled ? undefined : handleInputChange(id, event.target.value))}
              disabled={formDisabled}
              placeholder="Escriu aquí la resposta..."
            />
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'image':
        return (
          <div key={id} className="form-element-container image-element">
            {staticImages?.[id] && <img src={staticImages[id]} alt="Form" />}
          </div>
        );
      case 'copiable-text':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
            </div>
            <div className="transfer-row-value">{formatText(text)}</div>
            <button type="button" onClick={() => handleCopiedText(id, formatText(text))} className="editable-form-copy-button">
              {copiedText?.[id] ? 'Copiat!' : 'Copia el text'}
            </button>
          </div>
        );
      case 'image-upload':
        return renderImageUpload(element);
      case 'stripe-payment':
        // By card, that it is safe and, when the person pays it, what the commission is for
        if (totalPriceToPay === 0) return null;
        return (
          <div key={id} className="form-page-note">
            {cardPaymentNote(commissionMode === 'user_pays').map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
        );
      case 'text':
        return (
          <div key={id} className="form-element-container">
            <div className="rich-text" dangerouslySetInnerHTML={{ __html: formatRichText(content) }} />
          </div>
        );
      case 'assistance':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            <label className={`input-option ${formValues?.[id] === 1 ? 'selected' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="radio"
                name={id}
                checked={formValues?.[id] === 1}
                onChange={() => handleInputChange(id, 1)}
                disabled={formDisabled}
              />
              <span>{element.content?.yesLabel || 'Sí'}</span>
            </label>
            <label className={`input-option ${formValues?.[id] === 0 ? 'selected' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="radio"
                name={id}
                checked={formValues?.[id] === 0}
                onChange={() => handleInputChange(id, 0)}
                disabled={formDisabled}
              />
              <span>{element.content?.noLabel || 'No'}</span>
            </label>
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'ticket':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(options || []).map((option, index) => {
              const checked = formValues?.[id] === index;
              const optionId = `${id}_0`;
              const remaining = remainingTickets?.[optionId];
              const availability = renderTicketAvailability(remaining, checked ? 1 : 0);
              const disabled = formDisabled || (index === 0 && element.limit && remaining <= 0 && !checked);
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={id}
                    checked={checked || false}
                    onChange={() => (disabled ? undefined : handleInputChange(id, index))}
                    disabled={disabled}
                  />
                  <div className="ticket-option">
                    <span>{option}</span>
                    {index === 0 && element.limit && (
                      <span className="ticket-availability">{availability}</span>
                    )}
                  </div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'ticket-v2':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(content?.options || []).map((option, index) => {
              if (option.hidden) return null;
              const optionId = `${id}_${index}`;
              const quantity = formValues?.[id]?.[index] || 0;
              const remaining = remainingTickets?.[optionId];
              const availability = renderTicketAvailability(remaining, quantity);
              const disableIncrease = formDisabled || remaining <= 0;
              const disableDecrease = formDisabled || quantity <= 0;
              return (
                <div key={index} className="ticket-v2-row">
                  <div className="ticket-v2-info">
                    <span>{option.text}</span>
                    <span className="ticket-v2-meta">
                      {!Number.isNaN(Number(option.price)) ? <span className="ticket-price">{formatPriceDisplay(option.price)}</span> : null}
                      {option.limit && availability ? <span className="ticket-availability">{availability}</span> : null}
                    </span>
                  </div>
                  <div className="ticket-quantity">
                    <button
                      type="button"
                      className="editable-form-quantity-button"
                      aria-label={`Treu ${option.text}`}
                      disabled={disableDecrease}
                      onClick={() => handleTicketQuantityChange(id, index, -1)}
                    >
                      −
                    </button>
                    <span>{quantity}</span>
                    <button
                      type="button"
                      className="editable-form-quantity-button"
                      aria-label={`Afegeix ${option.text}`}
                      disabled={disableIncrease}
                      onClick={() => handleTicketQuantityChange(id, index, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'ticket-v2-single':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(content?.options || []).map((option, index) => {
              if (option.hidden) return null;
              const optionId = `${id}_${index}`;
              const quantity = formValues?.[id]?.[index] || 0;
              const remaining = remainingTickets?.[optionId];
              const availability = renderTicketAvailability(remaining, quantity);
              const checked = quantity === 1;
              const disabled = formDisabled || (option.limit && remaining <= 0 && !checked);
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleTicketQuantityChange(id, index, checked ? -1 : 1)}
                    disabled={disabled}
                  />
                  <div className="ticket-v2-single-option">
                    <div>
                      <div>{option.text}</div>
                      {option.limit && availability && (
                        <span className="ticket-availability">{availability}</span>
                      )}
                    </div>
                    {!Number.isNaN(Number(option.price)) && (
                      <div className={`ticket-price-badge ${checked ? 'selected' : ''}`}>
                        {formatPriceDisplay(option.price)}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      default:
        return null;
    }
  };

  const validateForm = () => {
    const requiredElements = getRequiredVisibleElements(formData, formValues, totalPriceToPay);

    const validationErrors = {};
    const filledFormValues = Object.fromEntries(Object.entries(formValues).filter(([, value]) => value !== undefined));

    requiredElements.forEach((element) => {
      const { id, type } = element;
      switch (type) {
        case 'multiple-choice':
        case 'ticket':
          if (!(id in filledFormValues) || filledFormValues[id] === undefined) {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'assistance':
          if (!(id in filledFormValues) || filledFormValues[id] === undefined) {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'checkbox':
          if (!(id in filledFormValues)) {
            validationErrors[id] = 'Aquest camp és obligatori';
          } else {
            const checked = Object.values(formValues[id] || {}).some((value) => value === true);
            if (!checked) validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'image-upload':
          if (uploadState[id] === 'uploading') {
            validationErrors[id] = "Espera que s'acabi de pujar la imatge";
          } else if (uploadState[id] !== 'uploaded') {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'short-answer':
        case 'paragraph':
          if (!(id in filledFormValues) || !String(formValues[id] || '').trim()) {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'ticket-v2-single':
        case 'ticket-v2':
          if (!(id in filledFormValues)) {
            validationErrors[id] = 'Aquest camp és obligatori';
          } else {
            const quantities = Object.values(formValues[id] || {});
            const totalQuantity = quantities.reduce((sum, quantity) => sum + (quantity || 0), 0);
            if (totalQuantity === 0) {
              validationErrors[id] = 'Has de comprar almenys un ticket';
            }
          }
          break;
        default:
          break;
      }
    });

    return validationErrors;
  };

  const uploadImagesIfNeeded = async (imageIds) => {
    for (const imgId of imageIds) {
      const base64 = formImagesValues[imgId];
      if (!base64) continue;
      setUploadState((prev) => ({ ...prev, [imgId]: 'uploading' }));
      try {
        // Wrap postAPI in a Promise to properly wait for the callback
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('Upload timeout'));
          }, 60000); // 60 second timeout for large images

          postAPI('/upload_form_image_user', {
            formId: evId,
            elementId: imgId,
            userId: user,
            base64,
          }, (response) => {
            clearTimeout(timeoutId);
            if (response?.success) {
              resolve(response);
            } else {
              reject(new Error(response?.error || 'Upload failed'));
            }
          });
        });
        setUploadState((prev) => ({ ...prev, [imgId]: 'uploaded' }));
      } catch (error) {
        setUploadState((prev) => ({ ...prev, [imgId]: 'error' }));
        throw error;
      }
    }
  };

  const elementsOrdered = useMemo(() => {
    if (!formData?.order || !Array.isArray(formData.order)) return elements;
    return [
      ...formData.order
        .map((elementId) => elements.find((element) => element.id === elementId))
        .filter(Boolean),
      ...elements.filter((element) => !formData.order.includes(element.id)),
    ];
  }, [elements, formData?.order]);

  // The payment page's elements (paymentScreen) are a step of their own when there
  // is something to pay, as in the app; otherwise only the questions are reachable.
  // The summary question isn't drawn: the bar at the bottom shows the total.
  const visibleElements = elementsOrdered.filter((element) => element.type !== 'resum-pagaments' && isElementVisible(element));
  const paymentStepElements = visibleElements.filter((element) => element.paymentScreen === true);
  const formStepElements = visibleElements.filter((element) => element.paymentScreen !== true);
  const hasPaymentStep = paymentStepElements.length > 0 && totalPriceToPay > 0;
  const onPaymentStep = hasPaymentStep && step === 'payment';
  const reachableElements = [...formStepElements, ...(hasPaymentStep ? paymentStepElements : [])];
  const saved = formSubmitted !== false || savedAnswer || sentHere;

  // A saved answer paid by card: what the card already paid (the server's count),
  // so the bar shows what is left to pay before sending. null while unknown.
  const [cardPaidBase, setCardPaidBase] = useState(null);
  const asksCardPaid = saved && onPaymentStep && paysByCard;
  useEffect(() => {
    if (!asksCardPaid) return undefined;
    let current = true;
    postAPI('/stripe/form-checkout/status', { formId: evId, userId: user }, (response) => {
      if (current && response?.success) setCardPaidBase(Number(response.paidBase) || 0);
    }, () => {});
    return () => { current = false; };
  }, [asksCardPaid, evId, user]);

  // Only what the person can see and answer can hold up a step (requiredAnswers.js)
  const errorsOn = (screenElements) => {
    const answerable = new Set(screenElements
      .filter((element) => canAnswerElement(element, { formValues, remainingTickets }))
      .map((element) => element.id));
    return Object.fromEntries(Object.entries(validateForm()).filter(([id]) => answerable.has(id)));
  };
  const formErrors = formDisabled ? {} : errorsOn(formStepElements);
  const sendErrors = formDisabled ? {} : errorsOn(reachableElements);
  const formMissing = firstMissingElement(formStepElements, formErrors);
  const sendMissing = firstMissingElement(reachableElements, sendErrors);

  // A tap before everything is answered: the first question missing comes into
  // view with what it needs (back to the questions when it is one of theirs)
  const showMissingAnswer = (missing, missingErrors) => {
    setErrors(missingErrors);
    if (onPaymentStep && missing.paymentScreen !== true) setStep('form');
    setMissingInView({ id: missing.id });
  };
  useEffect(() => {
    if (!missingInView) return;
    const question = Array.from(document.querySelectorAll('[data-element-id]'))
      .find((node) => node.dataset.elementId === String(missingInView.id));
    question?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [missingInView, step]);

  useEffect(() => {
    const onPopState = (event) => {
      if (event.state?.[PAYMENT_STEP_STATE] !== true) setStep('form');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const goToPaymentStep = () => {
    if (formMissing) {
      showMissingAnswer(formMissing, formErrors);
      return;
    }
    setErrors({});
    setStep('payment');
    window.history.pushState({ ...(window.history.state || {}), [PAYMENT_STEP_STATE]: true }, '');
    scrollToTop();
  };
  // The arrow at the top, as the browser's back
  const backToQuestions = () => {
    if (window.history.state?.[PAYMENT_STEP_STATE] === true) window.history.back();
    else setStep('form');
    scrollToTop();
  };

  const submitForm = async () => {
    if (sendMissing) {
      showMissingAnswer(sendMissing, sendErrors);
      return;
    }
    setErrors({});

    setIsSubmitting(true);

    const cleanedFormValues = createSubmitFormValues(formData, formValues);

    const sentImages = Object.keys(formImagesValues)
      .filter((key) => formImagesValues[key])
      .filter((key) => {
        const element = elements.find((el) => el.id === key);
        return element ? isElementVisible(element) : false;
      });

    try {
      await uploadImagesIfNeeded(sentImages);
    } catch (error) {
      alert('No s\'ha pogut pujar la imatge. Torna-ho a provar.');
      setIsSubmitting(false);
      return;
    }

    const guestInfo = {
      [user]: {
        nom: mote !== '' ? mote : 'VISITANT',
        cognom: mote !== '' ? mote : 'VISITANT',
        mote: mote !== '' ? mote : 'VISITANT',
      },
      ...castellersInfo,
    };

    try {
      await new Promise((resolve, reject) => {
        postAPI('/submit_form', {
          data: cleanedFormValues,
          userId: user,
          selectedEvent: evId,
          castellersInfo: isLoggedIn ? castellersInfo : guestInfo,
          formData,
          sentImages,
          // The server saves the ticket quantities itself, a guest's too, so they count towards the limits
          submissionOptions: { updateTickets: true },
        }, (response) => {
          if (response?.success) {
            resolve(response);
          } else {
            reject(new Error(response?.error || 'Submit failed'));
          }
        }, reject);
      });
    } catch (error) {
      alert('No s\'ha pogut enviar el formulari. Torna-ho a provar.');
      setIsSubmitting(false);
      return;
    }

    onSubmitted();
    setFormValues(cleanedFormValues);
    setStartCardPayment(paysByCard);
    setSent(true);
    setSentHere(true);
    setIsSubmitting(false);
    scrollToTop();
  };

  const resetForNewAnswer = () => {
    setMote('');
    onNewAnswer();
    window.scrollTo(0, 0);
  };

  // Buttons and selections in the colla's colour, as in the app
  const accentStyle = { '--form-accent': collaAccent() };
  const ticketLines = paymentLines(visibleFormTickets, formValues);
  const renderScreenElements = (screenElements) => screenElements.map((element) => {
    const rendered = renderElement(element);
    return rendered ? <div key={element.id} data-element-id={element.id}>{rendered}</div> : null;
  });

  if (sent) {
    // Says where the answer stands: still to pay by card is not "sent" yet for the person
    const renderSent = ({ phase = 'sent', message = null, paid = 0 } = {}) => {
      const thanks = mote ? `${mote}, gràcies per enviar la resposta.` : 'Gràcies per enviar la resposta.';
      const { title, text } = {
        opening: { title: 'Obrint el pagament...', text: 'Hem desat la teva resposta.' },
        confirming: { title: 'Confirmant el pagament...', text: 'Hem desat la teva resposta.' },
        owes: { title: 'Falta pagar', text: 'Hem desat la teva resposta. Paga amb targeta per acabar.' },
      }[phase] || { title: "S'ha enviat correctament!", text: thanks };
      return (
        <>
          <div className="editable-form-confirmation-container">
            <h2>{title}</h2>
            <p>{text}</p>
            {message ? <div className="form-error-text">{message}</div> : null}
          </div>
          {phase === 'paid' ? (
            <div className="form-element-container form-sent-paid">
              <span>Pagat amb targeta</span>
              <span>{formatMoney(paid)}€</span>
            </div>
          ) : null}
          {phase === 'opening' || phase === 'confirming' ? null : (
            <div className="form-element-container form-sent-actions">
              <button
                type="button"
                className="form-row-button"
                onClick={() => {
                  setStep('form');
                  setSent(false);
                }}
              >
                <span>Modifica la resposta</span>
                <Chevron />
              </button>
              <button type="button" className="form-row-button" onClick={resetForNewAnswer}>
                <span>Fes-ne una altra</span>
                <Chevron />
              </button>
            </div>
          )}
        </>
      );
    };
    return (
      <div className="form-container" style={accentStyle}>
        {showsCardPayment || paymentSession || paymentCancelled ? (
          <CardPayment
            evId={evId}
            user={user}
            paymentSession={paymentSession}
            autoStart={startCardPayment}
            lines={ticketLines}
            renderPage={renderSent}
          />
        ) : renderSent()}
      </div>
    );
  }

  const answering = isLoggedIn || isWarningAccepted;
  // What the bar adds up: the tickets, and on the payment step by card what the card
  // already paid (a saved answer) and the commission on what is left, when the person pays it
  // (paid in full already: the tickets and their total, as any answer)
  const paidBefore = saved ? cardPaidBase : 0;
  const cardBase = paidBefore === null ? 0 : Math.max(0, Math.round((totalPriceToPay - paidBefore) * 100) / 100);
  const byCardNow = onPaymentStep && paysByCard && paidBefore !== null && cardBase > 0.005;
  const commission = byCardNow ? cardCommission(cardBase, commissionMode, commissionRate) : 0;
  const barLines = [
    ...ticketLines,
    ...(byCardNow && paidBefore > 0 ? [{ key: 'already-paid', label: 'Ja pagat', amount: -paidBefore }] : []),
    ...(commission > 0 ? [{ key: 'card-commission', label: 'Comissió de la targeta', amount: commission }] : []),
  ];
  const barTotal = byCardNow ? cardBase + commission : totalPriceToPay;
  const missing = onPaymentStep || !hasPaymentStep ? sendMissing : formMissing;

  const renderBar = () => {
    if (!answering) {
      return (
        <FormBottomBar>
          <button type="button" onClick={() => setIsWarningAccepted(true)} className="editable-form-button">
            <span className="form-button-label">Respon des de la web<Chevron /></span>
          </button>
        </FormBottomBar>
      );
    }
    if (formDisabled) {
      // Answered: that it went through. Not answered: why it can't be sent. A hidden form, nothing
      const label = saved ? 'Enviat' : unavailableSubmitLabel({ outdated, notOpenYet });
      return label ? (
        <FormBottomBar>
          <button type="button" className="editable-form-button" disabled>{label}</button>
        </FormBottomBar>
      ) : null;
    }
    return (
      <FormBottomBar lines={barLines} total={barTotal} totalLabel={byCardNow && paidBefore > 0 ? 'Falta pagar' : 'Total'}>
        {/* Something still missing: it looks disabled, and a tap shows what */}
        <button
          type="button"
          onClick={hasPaymentStep && !onPaymentStep ? goToPaymentStep : submitForm}
          disabled={isSubmitting}
          aria-disabled={Boolean(missing)}
          className={`editable-form-button${missing ? ' editable-form-button-blocked' : ''}`}
        >
          {hasPaymentStep && !onPaymentStep
            ? <span className="form-button-label">Continua<Chevron /></span>
            : submitButtonLabel({ isSubmitting, saved, paysByCard: byCardNow, onPaymentStep })}
        </button>
      </FormBottomBar>
    );
  };

  // «Com et dius?»: someone of the colla (hidden ones too) is told the app avoids a second answer
  if (answering && !isLoggedIn && !nameGiven && !formDisabled && loginMember) {
    return (
      <div className="form-container" style={accentStyle}>
        <MemberLogin member={loginMember} onBack={() => setLoginMember(null)} />
      </div>
    );
  }
  if (answering && !isLoggedIn && !nameGiven && !formDisabled) {
    const typedName = String(mote || '').trim();
    const members = membersNamed(typedName, castellersInfo);
    const member = members.length > 0;
    return (
      <div className="form-container" style={accentStyle}>
        <button type="button" className="form-back-link" onClick={() => setIsWarningAccepted(false)}>
          <span aria-hidden="true">←</span> Torna
        </button>
        <h1>Com et dius?</h1>
        <p>Perquè la colla sàpiga de qui és la resposta.</p>
        <div className="form-element-container">
          <input
            type="text"
            value={mote}
            onChange={(event) => setMote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && typedName) setNameGiven(true);
            }}
            placeholder="Nom i cognoms"
            aria-label="El teu nom"
            autoComplete="name"
            autoFocus
          />
        </div>
        {member ? (
          <div className="form-element-container form-welcome">
            <h2>Ets tu?</h2>
            <p>Hi ha algú a la colla que es diu així. Si ets tu, entra: respondràs com a tu i la resposta no sortirà repetida.</p>
            {members.map((candidate) => (
              <button key={candidate.id} type="button" className="form-row-button form-member-row" onClick={() => setLoginMember(candidate)}>
                <span>{candidate.name}</span>
                <Chevron />
              </button>
            ))}
            <OpenInApp evId={evId} />
          </div>
        ) : null}
        <FormBottomBar>
          <button
            type="button"
            onClick={() => (typedName ? setNameGiven(true) : undefined)}
            aria-disabled={!typedName}
            className={`editable-form-button${typedName ? '' : ' editable-form-button-blocked'}`}
          >
            <span className="form-button-label">{member ? 'Continua igualment' : 'Continua'}<Chevron /></span>
          </button>
        </FormBottomBar>
      </div>
    );
  }

  if (onPaymentStep) {
    return (
      <div className="form-container" style={accentStyle}>
        <button type="button" className="form-back-link" onClick={backToQuestions} disabled={isSubmitting}>
          <span aria-hidden="true">←</span> Torna al formulari
        </button>
        <h1>Pagament</h1>
        <div style={formData?.hidden ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          {renderScreenElements(paymentStepElements)}
        </div>
        {renderBar()}
      </div>
    );
  }

  return (
    <div className="form-container" style={accentStyle}>
      <h1>{formData.title}</h1>
      <p dangerouslySetInnerHTML={{ __html: formatRichText(formData.description || '') }} />

      {!answering && (
        <div className="form-element-container form-welcome">
          <h2>Tens l'app de l'Aleta?</h2>
          <p>Si ja hi tens usuari, respon des de l'app: així la teva resposta no surt repetida.</p>
          <OpenInApp evId={evId} />
        </div>
      )}

      {answering && !isLoggedIn ? (
        <p className="form-answering-as">
          Respons com a <strong>{String(mote || '').trim() || 'visitant'}</strong>
          {formDisabled ? null : (
            <>
              {' · '}
              <button type="button" className="form-inline-link" onClick={() => setNameGiven(false)}>Canvia-ho</button>
            </>
          )}
        </p>
      ) : null}

      {answering && isLoggedIn && (
        <div className="form-element-container">
          <label>
            <div>El teu nom (estàs connectat com a tècnica/junta)</div>
            <input
              type="text"
              value={mote}
              onChange={(event) => setMote(event.target.value)}
              disabled={isLoggedIn}
              placeholder="Diga'ns qui ets..."
              style={{ fontSize: 14 }}
            />
          </label>

          {isLoggedIn && (
            <button
              onClick={resetForNewAnswer}
              className="editable-form-button editable-form-button-secondary"
              style={{
                marginTop: '10px'
              }}
            >
              Contestes per una altra persona?
            </button>
          )}
        </div>
      )}

      {formDisabled && (
        <div className="form-status-message">
          {formData?.hidden && '(Aquest formulari està ocult) '}
          {outdated && `El formulari va tancar el ${moment(formData?.closingDate).format('DD [de] MMM')} a les ${moment(formData?.closingDate).format('HH:mm')}`}
          {notOpenYet && `El formulari s'obrirà el ${moment(formData?.openingDate).format('DD [de] MMM')} a les ${moment(formData?.openingDate).format('HH:mm')}`}
        </div>
      )}

      {closeToEnding && (
        <div className="form-status-message">
          El formulari tancarà el {moment(formData?.closingDate).format('DD [de] MMM')} a les {moment(formData?.closingDate).format('HH:mm')} (En {
            moment(formData?.closingDate).diff(moment(), 'hours') < 1
              ? `${moment(formData?.closingDate).diff(moment(), 'minutes')} minuts`
              : moment(formData?.closingDate).diff(moment(), 'days') < 1
              ? `${moment(formData?.closingDate).diff(moment(), 'hours')} hores`
              : `${moment(formData?.closingDate).diff(moment(), 'days')} dies`
          })
        </div>
      )}

      {answering && (
        <div style={formData?.hidden ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          {renderScreenElements(formStepElements)}

          {/* Changing an answer already sent, another one instead; a new answer has nothing to leave */}
          {!formDisabled && saved && (
            <button
              type="button"
              onClick={resetForNewAnswer}
              className="editable-form-button editable-form-button-reset"
            >
              Fes-ne una altra
            </button>
          )}
        </div>
      )}
      {renderBar()}
    </div>
  );
};

export default EditableForm;
